import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import { getAccommodationListings, setAccommodationListingStatus, getListingCategories, getUser } from '../api/admin';
import { useCountry } from '../context/CountryContext';
import DocumentList from '../components/DocumentList';

const money = (cents, country) => {
  const amount = ((Number(cents) || 0) / 100).toLocaleString('en-US', { maximumFractionDigits: 0 });
  return country === 'KE' ? `KES ${amount}` : `$${amount}`;
};
const label = (v) => (v === undefined || v === null || v === '' ? null : String(v).replace(/_/g, ' '));
const bedsText = (beds = []) => beds.map((b) => `${b.count} × ${label(b.type)}`).join(', ');
// Structured answers from the listing wizard (Kayd, 20.10.2026), as review rows.
function detailRows(l) {
  const d = l.details || {};
  const allBeds = [...(d.beds || []), ...(d.bedrooms || []).flatMap((b) => b.beds || [])];
  return [
    ['Room category', d.room_category_name],
    ['Property', [label(d.property_type), label(d.space_type), d.entrance ? `${d.entrance} entrance` : null].filter(Boolean).join(' · ')],
    ['Shared areas', (d.shared_areas || []).map(label).join(', ')],
    ['Bedrooms', (d.bedrooms || []).length ? (d.bedrooms || []).map((b, i) => `#${i + 1}: ${bedsText(b.beds) || '—'}`).join(' · ') : null],
    ['Beds', !(d.bedrooms || []).length && allBeds.length ? bedsText(allBeds) : null],
    ['Other sleeping', d.extra_sleeping],
    ['Bathrooms', [d.bathrooms, label(d.bathroom_type)].filter((x) => x !== undefined && x !== null).join(' · ')],
    ['Kitchen', d.kitchen?.type ? [label(d.kitchen.type), label(d.kitchen.access)].filter(Boolean).join(', ') + ((d.kitchen_equipment || []).length ? ` — ${d.kitchen_equipment.map(label).join(', ')}` : '') : null],
    ['Furniture / laundry', [...(d.furniture || []), ...(d.laundry || [])].map(label).join(', ')],
    ['Outdoor', (d.outdoor || []).map((o) => `${label(o.type)} (${o.access})`).join(', ')],
    ['Hotel facilities', (d.hotel_facilities || []).map(label).join(', ')],
    ['Check-in / out', d.check_in_time || d.check_out_time ? `${d.check_in_time || '—'} / ${d.check_out_time || '—'}` : null],
    ['Rules', [d.smoking && `smoking: ${label(d.smoking)}`, d.pets && `pets: ${label(d.pets)}`, d.parties && `parties: ${label(d.parties)}`, d.quiet_hours && `quiet ${d.quiet_hours}`].filter(Boolean).join(' · ')],
    ['Key handover', label(d.key_handover)],
    ['Access (private)', l.private_details?.access_instructions],
    ['Cleaning fee', l.cleaning_fee_enabled === false ? 'none (included in price)' : null],
  ].filter(([, v]) => v);
}
const unitFor = (category) => (['wedding_hall', 'event'].includes(category) ? 'day' : 'night');

// Accommodation/Providers — one generic, filterable list view reused for
// "All Listings" and every per-category tab (Hotels/Vacation Rentals/
// Wedding Halls/Events) — the category tab just pre-fills the category_slug
// filter via the route, matching the brief's "each category must have its
// own filtered list view... All Listings must provide one combined list."
// On "All Listings" the category filter is populated from listing_categories
// (self-service — a category added via Marketplace Categories shows up here
// with no code change).
const AccommodationListings = () => {
  const { categorySlug } = useParams(); // undefined on the "All Listings" route
  const { country, isReadOnly, ready: countryReady } = useCountry();
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', city: '', created_from: '', created_to: '' });
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [reviewId, setReviewId] = useState(null);

  useEffect(() => { getListingCategories().then((res) => setCategories(res.data?.categories || [])); }, []);

  const load = () => {
    setLoading(true);
    const params = { ...filters };
    if (categorySlug) params.category_slug = categorySlug;
    if (country !== 'ALL') params.country = country;
    Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
    getAccommodationListings(params).then((res) => setListings(res.data?.listings || [])).finally(() => setLoading(false));
  };
  useEffect(() => { if (countryReady) load(); }, [categorySlug, country, filters, countryReady]); // eslint-disable-line

  const act = async (id, status, reason) => {
    try {
      await setAccommodationListingStatus(id, status, reason);
      toast.success(`Listing ${status}`);
      setRejectingId(null); setRejectReason('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Action failed');
    }
  };

  const title = categorySlug ? (categories.find((c) => c.slug === categorySlug)?.name || categorySlug) : 'All Listings';

  return (
    <div style={s.page}>
      <h1 style={s.h1}>{title}</h1>

      <div style={s.filters}>
        {!categorySlug && (
          <select style={s.select} value={filters.category_slug || ''} onChange={(e) => setFilters((f) => ({ ...f, category_slug: e.target.value }))}>
            <option value="">All categories</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
        )}
        <select style={s.select} value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="suspended">Suspended</option>
          <option value="draft">Drafts (not submitted)</option>
        </select>
        <input style={s.select} placeholder="City/location" value={filters.city} onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))} />
        <input style={s.select} type="date" value={filters.created_from} onChange={(e) => setFilters((f) => ({ ...f, created_from: e.target.value }))} title="Created from" />
        <input style={s.select} type="date" value={filters.created_to} onChange={(e) => setFilters((f) => ({ ...f, created_to: e.target.value }))} title="Created to" />
      </div>

      {isReadOnly && <div style={s.readOnlyBanner}>"All countries" is read-only — choose a single country in the header (paused countries are listed too) to approve, reject, or suspend a listing.</div>}

      {loading ? <p>Loading…</p> : (
        <div style={s.tableWrap}>
        <table style={s.table}>
          <thead><tr>
            <th style={s.th}>Title</th><th style={s.th}>Category</th><th style={s.th}>Country</th>
            <th style={s.th}>Provider</th><th style={s.th}>Status</th><th style={s.th}>Created</th><th style={s.th}>Actions</th>
          </tr></thead>
          <tbody>
            {listings.map((l) => (
              <tr key={l.id}>
                <td style={s.td}>{l.title}</td>
                <td style={s.td}>{l.category_info?.icon} {l.category_info?.name || l.category}</td>
                <td style={s.td}>{l.country}</td>
                <td style={s.td}>{l.host?.full_name || l.host?.email}</td>
                <td style={s.td}><span style={s.badge}>{l.status}</span></td>
                <td style={s.td}>{new Date(l.created_at).toLocaleDateString()}</td>
                <td style={s.td}>
                  <button style={s.btnGray} onClick={() => setReviewId(reviewId === l.id ? null : l.id)} aria-expanded={reviewId === l.id}>
                    {reviewId === l.id ? 'Close' : 'Review'}
                  </button>
                  {!isReadOnly && <Link to={`/accommodation/listings/edit/${l.id}`} style={{ ...s.btnGray, textDecoration: 'none', display: 'inline-block' }}>Edit</Link>}
                  {!isReadOnly && l.status !== 'approved' && <button style={s.btnGreen} onClick={() => act(l.id, 'approved')}>Approve</button>}
                  {!isReadOnly && l.status === 'approved' && <button style={s.btnGray} onClick={() => act(l.id, 'suspended')}>Suspend</button>}
                  {!isReadOnly && l.status !== 'rejected' && (
                    rejectingId === l.id ? (
                      <span>
                        <input style={s.smallInput} placeholder="Reason" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
                        <button style={s.btnRed} onClick={() => act(l.id, 'rejected', rejectReason)}>Confirm</button>
                      </span>
                    ) : <button style={s.btnRed} onClick={() => setRejectingId(l.id)}>Reject</button>
                  )}
                </td>
              </tr>
            )).flatMap((row, i) => {
              const l = listings[i];
              return reviewId === l.id
                ? [row, <tr key={`${l.id}-review`}><td colSpan={7} style={s.reviewCell}><ListingReview listing={l} /></td></tr>]
                : [row];
            })}
            {listings.length === 0 && <tr><td style={s.td} colSpan={7}>No listings found.</td></tr>}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

// Everything needed to decide on a listing (Kayd, 18.10.2026: "I also cannot
// view or review documents in those sections"): the listing itself, the
// provider's account and application status, and the provider's documents.
function ListingReview({ listing: l }) {
  const [host, setHost] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!l.host_id) return;
    getUser(l.host_id).then((res) => setHost(res.data)).catch((e) => setError(e.response?.data?.error || 'Could not load the provider.'));
  }, [l.host_id]);
  const app = host?.provider_application;
  // Pricing check inside the normal review (Kayd, 24.10.2026): the complete
  // customer price from the central calculation and any missing fee rule.
  const [price, setPrice] = useState(null);
  useEffect(() => {
    api.get(`/api/fees/listings/room/${l.id}/preview`, { params: { units: 3 } }).then((r) => setPrice(r.data?.preview)).catch(() => setPrice(null));
  }, [l.id]);
  const cur = l.country === 'KE' ? 'KES' : 'USD';
  const fm = (c) => `${cur} ${((Number(c) || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div style={s.review}>
      {price && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 13 }}>
          <strong>Pricing check (example for 3 {['wedding_hall', 'event'].includes(l.category) ? 'days' : 'nights'})</strong>
          <div>Base {fm(price.base)}{price.fees ? ` · extra services ${fm(price.fees)}` : ''}{price.taxes_online ? ` · taxes ${fm(price.taxes_online)}` : ''}{price.taxes_included ? ` · included taxes ${fm(price.taxes_included)}` : ''} · NULA-MOVE booking fee {['applied', 'individual'].includes(price.platform_fee_status) ? fm(price.platform_fee) : price.platform_fee_status === 'exempt' ? 'switched off' : '—'}</div>
          <div><strong>Total {fm(price.total)}</strong> · pay online now {fm(price.pay_online_now ?? price.online)} · pay on site {fm(0)}</div>
          {(price.warnings || []).map((w, i) => <div key={i} style={{ color: '#92400e', marginTop: 4 }}>⚠️ {w.message}{w.code === 'NO_PLATFORM_FEE_RULE' ? <> — <Link to="/fees">create it in Fees &amp; Taxes</Link></> : null}</div>)}
          {(price.lines || []).some((x) => x.needs_review) && <div style={{ color: '#92400e', marginTop: 4 }}>⚠️ A tax moved over from the earlier tax-rate field needs review — <Link to="/fees?tab=b">Fees &amp; Taxes, tab B</Link></div>}
        </div>
      )}
      <div style={s.reviewGrid}>
        <div>
          <h3 style={s.h3}>Listing</h3>
          {(l.photo_items?.length > 0 || l.photos?.length > 0) && (
            <div style={s.photos}>
              {(l.photo_items?.length ? l.photo_items : l.photos.map((url) => ({ url }))).map((p) => (
                <a key={p.url} href={p.url} target="_blank" rel="noopener noreferrer" style={s.photoLink} title={p.label || label(p.area) || ''}>
                  <img src={p.url} alt="" style={s.photo} />
                  {(p.area || p.is_cover) && <span style={s.photoTag}>{p.is_cover ? '★ ' : ''}{p.label || label(p.area)}</span>}
                </a>
              ))}
            </div>
          )}
          <dl style={s.dl}>
            <dt style={s.dt}>Price</dt><dd style={s.dd}>{money(l.price_per_night_cents, l.country)} / {l.booking_unit || unitFor(l.category)}{l.cleaning_fee_cents ? ` · cleaning ${money(l.cleaning_fee_cents, l.country)}` : ''}</dd>
            <dt style={s.dt}>Capacity</dt><dd style={s.dd}>up to {l.max_guests} guests · min. {l.min_stay_nights || 1} {unitFor(l.category)}(s)</dd>
            <dt style={s.dt}>Address</dt><dd style={s.dd}>{[l.address, l.city].filter(Boolean).join(', ') || '—'}{l.country ? ` (${l.country})` : ''}{l.lat != null ? ' · on map' : ''}</dd>
            <dt style={s.dt}>Booking</dt><dd style={s.dd}>{l.booking_mode === 'request' ? 'Booking request' : 'Instant booking'} · per {l.booking_unit || unitFor(l.category)}{l.units_count > 1 ? ` · ${l.units_count} units` : ''}{(l.time_slots || []).length ? ` · slots: ${l.time_slots.map((t) => `${t.label} ${t.start}–${t.end}`).join(', ')}` : ''}</dd>
            <dt style={s.dt}>Cancellation</dt><dd style={s.dd}>{l.cancellation_policy || 'standard'}</dd>
            {detailRows(l).map(([k, v]) => (<React.Fragment key={k}><dt style={s.dt}>{k}</dt><dd style={s.dd}>{v}</dd></React.Fragment>))}
            {(l.amenities || []).length > 0 && (<><dt style={s.dt}>Amenities</dt><dd style={s.dd}>{l.amenities.map((a) => a.replace(/_/g, ' ')).join(', ')}</dd></>)}
            {l.house_rules && (<><dt style={s.dt}>House rules</dt><dd style={s.dd}>{l.house_rules}</dd></>)}
            {l.location_details && (<><dt style={s.dt}>Area</dt><dd style={s.dd}>{l.location_details}</dd></>)}
            {l.description && (<><dt style={s.dt}>Description</dt><dd style={s.dd}>{l.description}</dd></>)}
            {l.rejection_reason && (<><dt style={s.dt}>Rejection reason</dt><dd style={s.dd}>{l.rejection_reason}</dd></>)}
          </dl>
        </div>
        <div>
          <h3 style={s.h3}>Provider</h3>
          {error && <p style={{ color: '#b91c1c', fontSize: 13 }}>{error}</p>}
          {!host && !error && <p style={{ fontSize: 13 }}>Loading…</p>}
          {host && (
            <>
              <p style={s.providerLine}>
                <strong>{host.user.full_name || host.user.email}</strong>
                {host.user.customer_number ? ` · #${host.user.customer_number}` : ''} · {host.user.email}
              </p>
              <p style={s.providerLine}>
                Account: {host.user.is_active ? 'active' : 'inactive'} · application: {app ? app.status : 'none'}
              </p>
              {app && app.status !== 'approved' && (
                <p style={s.warn}>The provider's application is not approved yet — review it under Host Applications first.</p>
              )}
              <DocumentList documents={host.documents} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1200 },
  tableWrap: { width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  reviewCell: { padding: 0, background: '#f8fafc', borderBottom: '1px solid #e5e7eb' },
  review: { padding: 16 },
  reviewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 },
  h3: { fontSize: 14, fontWeight: 700, margin: '0 0 8px' },
  photos: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
  photoLink: { position: 'relative', display: 'inline-block' },
  photoTag: { position: 'absolute', left: 2, bottom: 2, right: 2, background: 'rgba(17,24,39,0.7)', color: '#fff', fontSize: 10, padding: '1px 4px', borderRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  photo: { width: 96, height: 72, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb' },
  dl: { margin: 0, display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '6px 12px', fontSize: 13 },
  dt: { color: '#6b7280', fontWeight: 600 },
  dd: { margin: 0, color: '#111827', wordBreak: 'break-word' },
  providerLine: { fontSize: 13, margin: '0 0 6px', wordBreak: 'break-word' },
  warn: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px', margin: '0 0 8px' },
  h1: { fontSize: 22, fontWeight: 800, marginBottom: 16 },
  filters: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' },
  select: { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13 },
  readOnlyBanner: { background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: 10, borderRadius: 8, fontSize: 12.5, marginBottom: 14 },
  table: { width: '100%', minWidth: 760, borderCollapse: 'collapse', background: 'white', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 10px', borderBottom: '2px solid #e5e7eb', color: '#6b7280', fontSize: 12 },
  td: { padding: '8px 10px', borderBottom: '1px solid #f3f4f6' },
  badge: { padding: '3px 8px', borderRadius: 999, background: '#f3f4f6', fontSize: 11, fontWeight: 700 },
  btnGreen: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#16a34a', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnRed: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: 'none', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: 12 },
  btnGray: { padding: '5px 10px', marginRight: 4, borderRadius: 6, border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: 12 },
  smallInput: { padding: '5px 8px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, marginRight: 4, width: 120 },
};

export default AccommodationListings;
